import {MarkerClusterer} from "@googlemaps/markerclusterer";
import {applyFilters} from "@wordpress/hooks";

import {isPositionedAdvancedMarkerElement, PositionedAdvancedMarkerElement} from "./helpers";
import {InfoWindowManager, InfoWindowPositionResetMethod} from "./info-window-manager";
import {SearchIntegration} from "./search-integration";
import type {MarkerHandlerConfig} from "../view";

type AdvancedMarkerElement = google.maps.marker.AdvancedMarkerElement;
type AdvancedMarkerElementOptions = google.maps.marker.AdvancedMarkerElementOptions;

type MapElementClickedEventDetail = {
	marker: AdvancedMarkerElement,
	element: HTMLElement
}

type ManagedMarker = { marker: PositionedAdvancedMarkerElement, element: HTMLElement };
type ManagedMarkerWithDistance = ManagedMarker & { distance: number };
type ManagedMarkersList = (ManagedMarker|ManagedMarkerWithDistance)[];

export class MarkerHandler {
	private managedMarkers: ManagedMarkersList = [];
	private readonly clusterer: MarkerClusterer;
	private searchedLocationMarker: AdvancedMarkerElement|undefined;

	public constructor(
		private readonly queryLoopForm: HTMLFormElement,
		private readonly markerHandlerConfig: MarkerHandlerConfig,
		private readonly infoWindowManager: InfoWindowManager,
		private readonly searchIntegration: SearchIntegration,
		private readonly markerConstructor: new (args?: AdvancedMarkerElementOptions) => AdvancedMarkerElement,
		private readonly mapElement: HTMLElement,
		private readonly map: google.maps.Map
	) {
		this.clusterer = new MarkerClusterer({
			map,
			onClusterClick(event, cluster, map) {
				infoWindowManager.closeInfoWindow(InfoWindowPositionResetMethod.CLEAR);
				map.fitBounds(
					cluster.markers?.filter(isPositionedAdvancedMarkerElement)
						.reduce((bounds, marker) => bounds.extend(marker.position), new google.maps.LatLngBounds())
					?? new google.maps.LatLngBounds(),
					markerHandlerConfig.padding
				);
			}
		});

		map.addListener('bounds_changed', () => {
			const bounds = map.getBounds() ?? new google.maps.LatLngBounds();
			for (const {marker, element} of this.managedMarkers) {
				element.closest(".location")?.classList.toggle("marker-is-not-in-bounds", !bounds.contains(marker.position));
			}
		});

		const searchedLocationMarker = this.makeSearchedLocationMarker();
		if (searchedLocationMarker) {
			this.clusterer.addMarker(searchedLocationMarker, true);
		}

		document.addEventListener('queryLoopFormValueChange', this.refreshMarkersAndMap);
		document.addEventListener('queryLoopFormInitialized', this.refreshMarkersAndMap);
		this.refreshMarkersAndMap();
	}

	public makeSearchedLocationMarker() {
		const searchedLocationPosition = this.searchIntegration.getSearchedLocationPosition(true);
		if (searchedLocationPosition) {
			return this.searchedLocationMarker = new this.markerConstructor({
				position: searchedLocationPosition,
				content: this.markerHandlerConfig.buildIcon('searched-location')
			});
		} else {
			return this.searchedLocationMarker = undefined;
		}
	}

	public getBoundsForManagedMarkers(bonusPosition?: ConstructorParameters<typeof google.maps.LatLngBounds>[0], customMarkers?: ManagedMarkersList) {
		const markers = customMarkers ?? this.managedMarkers;
		if (markers.length === 0) {
			return new google.maps.LatLngBounds(bonusPosition).union(this.markerHandlerConfig.defaultBounds);
		}
		return markers.reduce((bounds, {marker}) => bounds.extend(marker.position), new google.maps.LatLngBounds(bonusPosition));
	}

	public getMinimumBounds(): google.maps.LatLngBounds {
		const searchedLatLng = this.searchIntegration.getSearchedLocationPosition();
		if (searchedLatLng) {
			if (this.markerHandlerConfig.minimumMarkers && this.markerHandlerConfig.minimumMarkers.count >= this.managedMarkers.length) {
				return this.getBoundsForManagedMarkers(searchedLatLng);
			}
			const nameAndDistanceOrderedMarkers = this.managedMarkers
				.map(managedMarker => ({
					...managedMarker,
					distance: Math.abs(google.maps.geometry.spherical.computeDistanceBetween(managedMarker.marker.position, searchedLatLng))
				}))
				.sort((a, b) => a.distance - b.distance);

			for (const distanceOrderedMarker of nameAndDistanceOrderedMarkers) {
				// This updates the displayed order of elements in the results column so that they are sorted by distance
				distanceOrderedMarker.element.parentElement?.parentElement?.appendChild(distanceOrderedMarker.element.parentElement);
			}

			// If the closest marker is farther away than the maximum distance at which we will display a slice of the markers, show all of them alongside the searched marker
			if (nameAndDistanceOrderedMarkers.length < 1 || nameAndDistanceOrderedMarkers[0].distance > this.markerHandlerConfig.maxSliceDistance || !this.markerHandlerConfig.minimumMarkers) {
				return this.getBoundsForManagedMarkers(searchedLatLng);
			}

			const minimumMarkers: ManagedMarkerWithDistance[] = [];
			for (const type of this.markerHandlerConfig.minimumMarkers.types) {
				for (let i = 0; i < nameAndDistanceOrderedMarkers.length; i++) {
					if (this.markerHandlerConfig.iconTyper(nameAndDistanceOrderedMarkers[i].element) === type) {
						minimumMarkers.push(...nameAndDistanceOrderedMarkers.splice(i, 1));
						break;
					}
				}
			}
			const remainingMinimumMarkerAmount = this.markerHandlerConfig.minimumMarkers.count - minimumMarkers.length;
			if (remainingMinimumMarkerAmount > 0) {
				minimumMarkers.push(...nameAndDistanceOrderedMarkers.slice(0, Math.min(remainingMinimumMarkerAmount, nameAndDistanceOrderedMarkers.length)));
			}
			return this.getBoundsForManagedMarkers(searchedLatLng, minimumMarkers);
		}

		return this.getBoundsForManagedMarkers();
	}

	public buildMarker(element: HTMLElement): PositionedAdvancedMarkerElement {
		const {lat, lng} = element.dataset;
		if (!lat || !lng) {
			throw new Error('All marker-creating elements must have data-lat and data-lng attributes')
		}
		const marker = new this.markerConstructor(applyFilters("plaudit.query-loop.google-maps-integration.marker-config", {
			position: {
				lat: parseFloat(lat),
				lng: parseFloat(lng)
			},
			content: this.markerHandlerConfig.buildIcon(element)
			//TODO: Should we calculate + include a title?
		} as AdvancedMarkerElementOptions) as AdvancedMarkerElementOptions) as PositionedAdvancedMarkerElement;
		marker.addListener('click', () => {
			element.scrollIntoView({behavior: "smooth", block: "center"});
			for (const locationElement of this.queryLoopForm.querySelectorAll<HTMLElement>('.wp-block-post-template .location')) {
				locationElement.classList.remove('location--selected');
			}
			element.closest('.location')?.classList.add('location--selected');
			this.infoWindowManager.openInfoWindow(marker, element);
			this.mapElement.dispatchEvent(new CustomEvent<MapElementClickedEventDetail>('MapElementClicked', {detail: {marker, element}}));
		});
		return marker;
	}

	public refreshMarkers() {
		if (this.searchedLocationMarker) {
			this.clusterer.removeMarker(this.searchedLocationMarker, true);
		}

		const searchedLocationMarker = this.makeSearchedLocationMarker();
		const newManagedMarkers: ManagedMarkersList = [];
		for (const element of this.queryLoopForm.querySelectorAll<HTMLElement>('[data-lat][data-lng]')) {
			const managedMarker: ManagedMarkersList[number] = {marker: this.buildMarker(element), element};
			newManagedMarkers.push(managedMarker);
		}

		this.infoWindowManager.closeInfoWindow(InfoWindowPositionResetMethod.CLEAR);
		this.clusterer.removeMarkers(this.managedMarkers.map(({marker}) => marker), true);
		this.managedMarkers = newManagedMarkers;

		if (searchedLocationMarker) {
			this.clusterer.addMarker(searchedLocationMarker, true);
		}
		this.clusterer.addMarkers(newManagedMarkers.map(({marker}) => marker));
	}

	public readonly refreshMarkersAndMap = () => {
		this.refreshMarkers();
		this.map.fitBounds(this.getMinimumBounds(), this.markerHandlerConfig.padding);
	};
}
